import { useEffect, useMemo } from 'react'
import { format, startOfDay, endOfDay } from 'date-fns'
import { useQueryState, parseAsString } from 'nuqs'

import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import ActiveUsersListing from '@/modules/dashboard/components/ActiveUsers/components/ActiveUsersListing'
import useQueryGetActiveUsers from '@/modules/dashboard/api/hooks/useQueryGetActiveUsers'
import useQueryGetActiveUsersSummary from '@/modules/dashboard/api/hooks/useQueryGetActiveUsersSummary'

import type {
	GetActiveUsersResponse,
	GetActiveUsersWithComparisonResponse,
	GetActiveUsersSummaryWithComparisonResponse,
	GetActiveUsersSummaryResponse,
} from '@/modules/dashboard/type'

function ActiveUsersPage() {
	const { dateFilter, setDateFilter } = useDateFilter()
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(''),
	)

	const [enableComparison, setEnableComparison] = useQueryState(
		'compare',
		parseAsString.withDefault(''),
	)
	const [compareStartDate, setCompareStartDate] = useQueryState(
		'compareStart',
		parseAsString.withDefault(''),
	)
	const [compareEndDate, setCompareEndDate] = useQueryState(
		'compareEnd',
		parseAsString.withDefault(''),
	)
	const [compareDateRange, setCompareDateRange] = useQueryState(
		'compareDateRange',
		parseAsString.withDefault(''),
	)

	useEffect(() => {
		// Only set today's date if there are no dates AND dateRange is not 'all'
		if (!dateFilter.startDate && !dateFilter.endDate && dateRange !== 'all') {
			const today = new Date()
			setDateFilter({
				startDate: format(startOfDay(today), 'yyyy-MM-dd'),
				endDate: format(endOfDay(today), 'yyyy-MM-dd'),
			})
			setDateRange('today')
		}
	}, [
		dateFilter.startDate,
		dateFilter.endDate,
		dateRange,
		setDateFilter,
		setDateRange,
	])

	// Auto-set comparison dates when comparison is enabled
	useEffect(() => {
		if (enableComparison === 'true' && (!compareStartDate || !compareEndDate)) {
			// Auto-select previous period (7 days before current period)
			if (dateFilter.startDate && dateFilter.endDate) {
				const start = new Date(dateFilter.startDate)
				const end = new Date(dateFilter.endDate)
				const daysDiff = Math.ceil(
					(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
				)

				// Go back the same number of days
				const compareStart = new Date(start)
				compareStart.setDate(compareStart.getDate() - daysDiff - 1)
				const compareEnd = new Date(end)
				compareEnd.setDate(compareEnd.getDate() - daysDiff - 1)

				setCompareStartDate(format(compareStart, 'yyyy-MM-dd'))
				setCompareEndDate(format(compareEnd, 'yyyy-MM-dd'))
			}
		}
	}, [
		enableComparison,
		compareStartDate,
		compareEndDate,
		dateFilter.startDate,
		dateFilter.endDate,
		setCompareStartDate,
		setCompareEndDate,
	])

	// Build comparison dates from user selection
	const comparisonDates = useMemo(() => {
		if (enableComparison !== 'true' || !compareStartDate || !compareEndDate) {
			return { compareStart: undefined, compareEnd: undefined }
		}

		return {
			compareStart: `${compareStartDate}T00:00:00.000Z`,
			compareEnd: `${compareEndDate}T23:59:59.999Z`,
		}
	}, [enableComparison, compareStartDate, compareEndDate])

	const payload = {
		startDate: dateFilter.startDate
			? `${dateFilter.startDate}T00:00:00.000Z`
			: undefined,
		endDate: dateFilter.endDate
			? `${dateFilter.endDate}T23:59:59.999Z`
			: undefined,
		interval: '30min',
		...comparisonDates,
	}

	// Fetch real data from API
	const { data: activeUsersResponse, isLoading: isLoadingActiveUsers } =
		useQueryGetActiveUsers(payload) as {
			data:
				| GetActiveUsersResponse
				| GetActiveUsersWithComparisonResponse
				| undefined
			isLoading: boolean
		}

	const { data: summaryResponse, isLoading: isLoadingSummary } =
		useQueryGetActiveUsersSummary(payload) as {
			data:
				| GetActiveUsersSummaryWithComparisonResponse
				| GetActiveUsersSummaryResponse
				| undefined
			isLoading: boolean
			refetch: () => void
		}

	// Extract active users data from response
	// API returns different formats based on comparison mode:
	// - With comparison: { current: { data: [...], startDate, endDate }, comparison: {...}, growth: {...} }
	// - Without comparison: { data: [...], total, startDate, endDate, interval }
	const activeUsersData =
		activeUsersResponse && 'current' in activeUsersResponse
			? activeUsersResponse.current
			: activeUsersResponse

	const comparisonActiveUsersData =
		activeUsersResponse && 'current' in activeUsersResponse
			? activeUsersResponse.comparison
			: undefined

	// Extract summary data from response
	const summaryData =
		summaryResponse && 'current' in summaryResponse
			? summaryResponse.current
			: summaryResponse
	const comparisonSummary =
		summaryResponse && 'current' in summaryResponse
			? summaryResponse.comparison
			: undefined

	const isLoading = isLoadingActiveUsers || isLoadingSummary

	return (
		<PageContainer>
			<ActiveUsersListing
				data={activeUsersData}
				comparisonData={comparisonActiveUsersData}
				summaryData={summaryData}
				comparisonSummary={comparisonSummary}
				isLoading={isLoading}
				enableComparison={enableComparison === 'true'}
				onToggleComparison={() =>
					setEnableComparison(enableComparison === 'true' ? '' : 'true')
				}
				compareStartDate={compareStartDate}
				compareEndDate={compareEndDate}
				onCompareStartChange={setCompareStartDate}
				onCompareEndChange={setCompareEndDate}
				compareDateRange={compareDateRange}
				onCompareDateRangeChange={setCompareDateRange}
			/>
		</PageContainer>
	)
}

const GuardedActiveUsersPage = withUserPermission(ActiveUsersPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_MARKETING],
})

export default GuardedActiveUsersPage
